import { z } from "zod";
import { MAX_ATTACHMENT_SIZE_MB } from "variables/configuratorSupport";

export enum SupportInquiryType {
  BUG = "BUG",
  FEEDBACK = "FEEDBACK",
}

export const SupportInquirySchema = z.object({
  type: z
    .enum([SupportInquiryType.BUG, SupportInquiryType.FEEDBACK])
    .default(SupportInquiryType.BUG),
  email: z
    .string()
    .refine(
      (val) => !val || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val),
      "INVALID_EMAIL_FORMAT",
    ),
  subject: z.string().min(4, "SUBJECT_TOO_SHORT").max(50, "SUBJECT_TOO_LONG"),
  message: z
    .string()
    .min(5, "MESSAGE_TOO_SHORT")
    .max(30000, "MESSAGE_TOO_LONG"),
  attachments: z.array(z.instanceof(File).optional()).optional().default([]),
});

export type SupportInquiry = z.infer<typeof SupportInquirySchema>;

export const SUPPORT_INQUIRY_INITIAL_VALUES: SupportInquiry = {
  type: SupportInquiryType.BUG,
  email: "",
  subject: "",
  message: "",
  attachments: [],
};

export const ALLOWED_FILE_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/jpg",
  "text/plain",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

export const validateTotalAttachments = (
  newFiles: File[],
  existingFiles: File[] = [],
): string | null => {
  const total = [...existingFiles, ...newFiles].reduce(
    (acc, { size }) => acc + size,
    0,
  );

  if (total > MAX_ATTACHMENT_SIZE_MB * 1024 * 1024) {
    return "MAX_UPLOAD_SIZE_EXCEEDED";
  }

  return null;
};

export const validateAttachment = (file: File): string | null => {
  if (!file) {
    return null;
  }
  const fileSizeMB = file.size / (1024 * 1024);
  if (fileSizeMB > MAX_ATTACHMENT_SIZE_MB) {
    return `FILE_TOO_LARGE_${MAX_ATTACHMENT_SIZE_MB}MB`;
  }

  if (!ALLOWED_FILE_TYPES.includes(file.type)) {
    return "FILE_TYPE_NOT_ALLOWED";
  }

  return null;
};
